import React from "react";
import {
  Box,
  ButtonBase,
  Dialog,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { Campaign, ChevronRight, Close, EditNote, SmartDisplay } from "@mui/icons-material";
import { useTranslatedDictionary } from "./hooks/useTranslatedDictionary";

const SELECTOR_TEXT = {
  es: {
    title: "¿Qué quieres crear?",
    post: "Publicación",
    postDesc: "Texto, fotos o videos en el inicio",
    reel: "Reel",
    reelDesc: "Un video para la sección de Reels",
    promotion: "Promoción",
    promotionDesc: "Un anuncio para llegar a más personas",
  },
  en: {
    title: "What do you want to create?",
    post: "Post",
    postDesc: "Text, photos or videos on the home feed",
    reel: "Reel",
    reelDesc: "A video for the Reels section",
    promotion: "Promotion",
    promotionDesc: "An ad to reach more people",
  },
};

const OPTIONS = [
  { type: "post", Icon: EditNote, titleKey: "post", descKey: "postDesc" },
  { type: "reel", Icon: SmartDisplay, titleKey: "reel", descKey: "reelDesc" },
  { type: "promotion", Icon: Campaign, titleKey: "promotion", descKey: "promotionDesc" },
];

const PublicarSelector = ({ open, onClose, onSelect, language = "es", themeMode = "oscuro" }) => {
  const t = useTranslatedDictionary(SELECTOR_TEXT, language);
  const isLightTheme = themeMode === "claro";
  const onSurface = (opacity = 1) =>
    isLightTheme ? `rgba(15,20,32,${opacity})` : `rgba(255,255,255,${opacity})`;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      sx={{ "& .MuiDialog-container": { alignItems: { xs: "flex-end", sm: "center" } } }}
      PaperProps={{
        sx: {
          m: { xs: 0, sm: 4 },
          boxSizing: "border-box",
          width: "100%",
          borderRadius: { xs: "22px 22px 0 0", sm: "22px" },
          bgcolor: isLightTheme ? "#fff" : "#141414",
          backgroundImage: "none",
          color: onSurface(1),
          p: 2,
        },
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5, pl: 0.5 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {t("title")}
        </Typography>
        <IconButton onClick={onClose} sx={{ color: onSurface(1) }}>
          <Close />
        </IconButton>
      </Stack>

      <Stack spacing={1}>
        {OPTIONS.map(({ type, Icon, titleKey, descKey }) => (
          <ButtonBase
            key={type}
            onClick={() => onSelect(type)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              p: 1.5,
              borderRadius: "16px",
              textAlign: "left",
              bgcolor: onSurface(0.04),
              border: `1px solid ${onSurface(0.08)}`,
              "&:hover": { borderColor: "#00d4ff", bgcolor: "rgba(0,212,255,0.08)" },
            }}
          >
            <Box
              sx={{
                width: 44,
                height: 44,
                flexShrink: 0,
                borderRadius: "12px",
                bgcolor: "rgba(0,212,255,0.14)",
                display: "grid",
                placeItems: "center",
              }}
            >
              <Icon sx={{ color: "#00d4ff" }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontWeight: 700 }}>{t(titleKey)}</Typography>
              <Typography variant="body2" sx={{ color: onSurface(0.65) }}>
                {t(descKey)}
              </Typography>
            </Box>
            <ChevronRight sx={{ color: onSurface(0.45) }} />
          </ButtonBase>
        ))}
      </Stack>
    </Dialog>
  );
};

export default PublicarSelector;
